import { Autocomplete, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from "@mui/material";
import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";

import {
  EXPENSE_CATEGORY_OPTIONS,
  formatRevenueExpenseCategory,
  normalizeRevenueExpenseCategory,
} from "./revenueExpenseCategories";
import { deleteRevenueExpenseCategoryLimit, upsertRevenueExpenseCategoryLimit } from "./revenueExpensesApi";

const EXPENSE_TYPE = 2;

const buildUniqueCategoryOptions = (currentCategory) => {
  const mergedOptions = [...EXPENSE_CATEGORY_OPTIONS];

  if (
    currentCategory &&
    !mergedOptions.some((option) => normalizeRevenueExpenseCategory(option.value) === normalizeRevenueExpenseCategory(currentCategory))
  ) {
    mergedOptions.unshift({ value: currentCategory });
  }

  return mergedOptions;
};

function RevenueExpenseCategoryLimitModal({
  open,
  onClose,
  onSaved,
  onDeleted,
  openSnackBar,
  initialCategory,
  initialMaxValue,
  hasExistingLimit,
  lockCategory,
}) {
  const [category, setCategory] = useState("");
  const [maxValue, setMaxValue] = useState("");
  const [activeAction, setActiveAction] = useState(null);
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const isSubmitting = activeAction !== null;

  const fieldSx = useMemo(
    () => ({
      "& .MuiInputLabel-root": {
        color: darkMode ? "rgba(226, 232, 240, 0.72)" : undefined,
      },
      "& .MuiInputLabel-root.Mui-focused": {
        color: darkMode ? "#f8fafc" : undefined,
      },
      "& .MuiOutlinedInput-root": {
        color: darkMode ? "#f8fafc" : undefined,
        backgroundColor: darkMode ? "rgba(15, 23, 42, 0.48)" : undefined,
        "& fieldset": {
          borderColor: darkMode ? "rgba(148, 163, 184, 0.32)" : undefined,
        },
        "&:hover fieldset": {
          borderColor: darkMode ? "rgba(148, 163, 184, 0.54)" : undefined,
        },
        "&.Mui-focused fieldset": {
          borderColor: darkMode ? "rgba(96, 165, 250, 0.95)" : undefined,
        },
      },
      "& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator": {
        color: darkMode ? "rgba(226, 232, 240, 0.72)" : undefined,
      },
      "& .MuiOutlinedInput-input::placeholder": {
        color: darkMode ? "rgba(148, 163, 184, 0.85)" : undefined,
        opacity: 1,
      },
    }),
    [darkMode]
  );

  const categoryOptions = useMemo(
    () => buildUniqueCategoryOptions(initialCategory),
    [initialCategory]
  );

  useEffect(() => {
    if (!open) {
      return;
    }

    setCategory(initialCategory ?? "");
    setMaxValue(initialMaxValue != null ? String(initialMaxValue) : "");
  }, [initialCategory, initialMaxValue, open]);

  const selectedCategoryOption = useMemo(
    () =>
      categoryOptions.find(
        (option) => normalizeRevenueExpenseCategory(option.value) === normalizeRevenueExpenseCategory(category)
      ) ?? null,
    [category, categoryOptions]
  );

  const handleSave = async () => {
    const normalizedCategory = category.trim();
    const numericMaxValue = Number(maxValue);

    if (!normalizedCategory) {
      openSnackBar?.({ type: "error", message: "Selecione uma categoria para configurar o limite." });
      return;
    }

    if (!numericMaxValue || numericMaxValue <= 0) {
      openSnackBar?.({ type: "error", message: "Informe um valor máximo maior que zero." });
      return;
    }

    setActiveAction("save");

    try {
      const savedItem = await upsertRevenueExpenseCategoryLimit({
        type: EXPENSE_TYPE,
        category: normalizedCategory,
        maxValue: numericMaxValue,
      });

      onSaved?.(savedItem);
      openSnackBar?.({ type: "success", message: "Limite da categoria salvo com sucesso." });
      onClose?.();
    } catch (error) {
      openSnackBar?.({ type: "error", message: `${error}` });
    } finally {
      setActiveAction(null);
    }
  };

  const handleDelete = async () => {
    const normalizedInitialCategory = initialCategory.trim();

    if (!normalizedInitialCategory) {
      openSnackBar?.({ type: "error", message: "Não foi possível identificar a categoria para remover o limite." });
      return;
    }

    setActiveAction("delete");

    try {
      await deleteRevenueExpenseCategoryLimit({
        type: EXPENSE_TYPE,
        category: normalizedInitialCategory,
      });

      onDeleted?.(normalizedInitialCategory);
      openSnackBar?.({ type: "success", message: "Limite da categoria removido com sucesso." });
      onClose?.();
    } catch (error) {
      openSnackBar?.({ type: "error", message: `${error}` });
    } finally {
      setActiveAction(null);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={isSubmitting ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          backgroundColor: darkMode ? "#0f172a" : "#ffffff",
          color: darkMode ? "#f8fafc" : undefined,
          backgroundImage: "none",
          border: darkMode ? "1px solid rgba(148, 163, 184, 0.2)" : undefined,
          boxShadow: darkMode ? "0 24px 80px rgba(2, 6, 23, 0.5)" : undefined,
        },
      }}
      BackdropProps={{
        sx: {
          backgroundColor: darkMode ? "rgba(2, 6, 23, 0.72)" : undefined,
        },
      }}
    >
      <DialogTitle
        sx={{
          color: darkMode ? "#f8fafc" : undefined,
          borderBottom: darkMode ? "1px solid rgba(148, 163, 184, 0.16)" : undefined,
          pb: 2,
        }}
      >
        Configurar limite por categoria
      </DialogTitle>
      <DialogContent sx={{ pt: "20px !important" }}>
        <MDTypography
          variant="button"
          color="text"
          sx={{ display: "block", mb: 2, color: darkMode ? "rgba(226, 232, 240, 0.78)" : undefined }}
        >
          Defina o valor máximo mensal que você quer acompanhar para esta categoria de despesa.
        </MDTypography>

        <MDBox mb={2}>
          <Autocomplete
            freeSolo
            disabled={lockCategory}
            options={categoryOptions}
            value={selectedCategoryOption ?? (category || null)}
            isOptionEqualToValue={(option, value) => {
              if (typeof value === "string") {
                return normalizeRevenueExpenseCategory(option.value) === normalizeRevenueExpenseCategory(value);
              }

              return normalizeRevenueExpenseCategory(option.value) === normalizeRevenueExpenseCategory(value?.value);
            }}
            getOptionLabel={(option) => {
              if (typeof option === "string") {
                return option;
              }

              return formatRevenueExpenseCategory(option.value);
            }}
            onChange={(_, newValue) => {
              if (typeof newValue === "string") {
                setCategory(newValue);
                return;
              }

              setCategory(newValue?.value ?? "");
            }}
            onInputChange={(_, newInputValue, reason) => {
              if (!lockCategory && (reason === "input" || reason === "clear")) {
                setCategory(newInputValue);
              }
            }}
            renderOption={(props, option) => (
              <li {...props} key={option.value}>
                {formatRevenueExpenseCategory(option.value)}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Categoria"
                placeholder="Selecione ou digite uma categoria"
                sx={fieldSx}
              />
            )}
            slotProps={{
              paper: {
                sx: {
                  backgroundColor: darkMode ? "#0f172a" : "#ffffff",
                  color: darkMode ? "#f8fafc" : undefined,
                  border: darkMode ? "1px solid rgba(148, 163, 184, 0.2)" : undefined,
                  boxShadow: darkMode ? "0 20px 60px rgba(2, 6, 23, 0.45)" : undefined,
                },
              },
            }}
            ListboxProps={{
              sx: {
                backgroundColor: darkMode ? "#0f172a" : "#ffffff",
                color: darkMode ? "#f8fafc" : undefined,
                "& .MuiAutocomplete-option": {
                  color: darkMode ? "#f8fafc" : undefined,
                },
                "& .MuiAutocomplete-option[aria-selected='true']": {
                  backgroundColor: darkMode ? "rgba(59, 130, 246, 0.18)" : undefined,
                },
                "& .MuiAutocomplete-option.Mui-focused": {
                  backgroundColor: darkMode ? "rgba(148, 163, 184, 0.16)" : undefined,
                },
              },
            }}
          />
        </MDBox>

        <MDInput
          type="number"
          label="Valor máximo mensal"
          fullWidth
          value={maxValue}
          onChange={(event) => setMaxValue(event.target.value)}
          sx={fieldSx}
        />
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          pb: 3,
          pt: 2,
          borderTop: darkMode ? "1px solid rgba(148, 163, 184, 0.16)" : undefined,
        }}
      >
        {hasExistingLimit && (
          <MDButton
            variant="text"
            color="error"
            onClick={handleDelete}
            disabled={isSubmitting}
            sx={{ mr: "auto" }}
          >
            {activeAction === "delete" ? <CircularProgress color="inherit" size={16} /> : "Remover limite"}
          </MDButton>
        )}
        <MDButton variant="outlined" color="secondary" onClick={onClose} disabled={isSubmitting}>
          Cancelar
        </MDButton>
        <MDButton variant="gradient" color="success" onClick={handleSave} disabled={isSubmitting}>
          {activeAction === "save" ? <CircularProgress color="inherit" size={16} /> : "Salvar limite"}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

RevenueExpenseCategoryLimitModal.defaultProps = {
  hasExistingLimit: false,
  initialCategory: "",
  initialMaxValue: "",
  lockCategory: false,
  onClose: undefined,
  onDeleted: undefined,
  onSaved: undefined,
  openSnackBar: undefined,
};

RevenueExpenseCategoryLimitModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  onSaved: PropTypes.func,
  onDeleted: PropTypes.func,
  openSnackBar: PropTypes.func,
  hasExistingLimit: PropTypes.bool,
  initialCategory: PropTypes.string,
  initialMaxValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  lockCategory: PropTypes.bool,
};

export default RevenueExpenseCategoryLimitModal;
